import { motion } from "framer-motion";
import { Package, Users, Crown, Zap, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { useClients } from "@/hooks/useClients";
import { PageTour } from "@/components/tour/PageTour";
import { packagesSteps } from "@/lib/tour-steps";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, Cell } from "recharts";

export default function Packages() {
  const { clients, getClientStats } = useClients();
  const stats = getClientStats();

  const standardClients = clients.filter(c => c.package === 'standard');
  const eliteClients = clients.filter(c => c.package === 'elite');

  const standardChurn = standardClients.length > 0
    ? (standardClients.filter(c => c.status === 'churned').length / standardClients.length) * 100
    : 0;
  const eliteChurn = eliteClients.length > 0
    ? (eliteClients.filter(c => c.status === 'churned').length / eliteClients.length) * 100
    : 0;

  // Real client status distribution from current data
  const activeCount = clients.filter(c => c.status === 'active').length;
  const pausedCount = clients.filter(c => c.status === 'paused').length;
  const churnedCount = clients.filter(c => c.status === 'churned').length;

  const statusDistribution = [
    { status: "Active", count: activeCount, color: "#22c55e" },
    { status: "Paused", count: pausedCount, color: "#f59e0b" },
    { status: "Churned", count: churnedCount, color: "#ef4444" },
  ];

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={packagesSteps} />
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Packages</h1>
        <p className="text-muted-foreground">Package performance and client distribution</p>
      </div>

      {/* Package Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Standard Package */}
        <VentureCard>
          <VentureCardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <VentureCardTitle>Standard Package</VentureCardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Entry-level package: $3,150 setup fee + $150/demo (first 3 demos free)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardHeader>
          <VentureCardContent className="space-y-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold">$3,150</span>
              <span className="text-muted-foreground">setup</span>
              <span className="text-xl font-bold ml-4">+ $150</span>
              <span className="text-muted-foreground">/demo</span>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <p className="text-sm text-muted-foreground">Active Clients</p>
                <p className="text-2xl font-bold">{stats.standard}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Churn Rate</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{standardChurn.toFixed(1)}%</p>
                  <BadgeVenture variant={standardChurn < 10 ? "success" : "warning"}>
                    {standardChurn < 10 ? "Healthy" : "Watch"}
                  </BadgeVenture>
                </div>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>

        {/* Elite Package */}
        <VentureCard className="border-amber-500/30">
          <VentureCardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <VentureCardTitle>Elite Package</VentureCardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Premium package: $5,150 setup fee + $200/demo + dedicated closer + 7.5% revenue share above $50K</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardHeader>
          <VentureCardContent className="space-y-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold">$5,150</span>
              <span className="text-muted-foreground">setup</span>
              <span className="text-xl font-bold ml-4">+ $200</span>
              <span className="text-muted-foreground">/demo</span>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <p className="text-sm text-muted-foreground">Active Clients</p>
                <p className="text-2xl font-bold">{stats.elite}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Churn Rate</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{eliteChurn.toFixed(1)}%</p>
                  <BadgeVenture variant={eliteChurn < 10 ? "success" : "warning"}>
                    {eliteChurn < 10 ? "Healthy" : "Watch"}
                  </BadgeVenture>
                </div>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Client Status Distribution */}
      <VentureCard>
        <VentureCardHeader>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-primary" />
            <VentureCardTitle>Client Status Distribution</VentureCardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Breakdown of client statuses across your portfolio</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusDistribution} layout="vertical" barSize={30}>
                <XAxis type="number" stroke="#525252" fontSize={12} allowDecimals={false} />
                <YAxis dataKey="status" type="category" stroke="#525252" fontSize={12} width={80} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #262626",
                    borderRadius: "8px",
                    color: "#fff"
                  }}
                  formatter={(value: number) => [value, 'Clients']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {statusDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 p-4 rounded-lg bg-surface border border-border">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Based on current client statuses.
              Total clients: {clients.length} ({activeCount} active, {pausedCount} paused, {churnedCount} churned).
            </p>
          </div>
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
